import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

import TextAreaField from '../../../../FormLegacy/FormFieldsV2/TextAreaField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import Stepper from '../../../../Stepper/Stepper'
import API from '../../../../../api/api'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import { ENTRY_NOTE_MAX_LENGTH } from '../../constants'
import ScheduledDateTimeFields from '../../../ScheduledDateTimeFields/ScheduledDateTimeFields'
import { useScheduledDateTime } from '../../../ScheduledDateTimeFields/useScheduledDateTime'

const INITIAL_FORM_STATE = {
  category: null,
  description: '',
  problemId: null,
  locationId: null,
  pets: false,
  permissionToEnter: true,
  entryNote: '',
}

const EntrataSettings = ({
  building,
  resident,
  assignee,
  onBack,
  onCancel,
  voicemailChatMessageSid,
  inspectionId,
  description,
  checklistRoomItemId,
  onSuccess,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const scheduledDateTime = useScheduledDateTime({ timezone: building.timezone })
  const { displaySnackbar } = useSnackbar()
  const [formState, setFormState] = useState(INITIAL_FORM_STATE)
  const [entrataConfig, setEntrataConfig] = useState({ categories: [], issues: [], locations: [] })
  const [filterByCategory, setFilterByCategory] = useState(false)
  const [filterByLocation, setFilterByLocation] = useState(false)
  const [formErrors, setFormErrors] = useState({})

  const handleFieldChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleEntryNoteChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))

    // Validate entry note length
    if (value.length > ENTRY_NOTE_MAX_LENGTH) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: t('maintenance:form.pmsSettings.entryNote.validation', {
          maxLength: ENTRY_NOTE_MAX_LENGTH,
        }),
      }))
    } else {
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormState((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSubmit = async () => {
    if (!scheduledDateTime.validate()) return

    const requestData = {
      buildingId: building.id,
      entrata: true,
      description: formState.description,
      enterPermission: formState.permissionToEnter,
      pets: formState.pets,
      entryNote: formState.entryNote,
      ...(formState.category && { category: formState.category.value }),
      ...(assignee && { assignee: assignee.id }),
      ...(formState.problemId && { problemId: formState.problemId.value }),
      ...(formState.locationId && { locationId: formState.locationId.value }),
      ...(scheduledDateTime.timestamp && { scheduledDate: scheduledDateTime.timestamp }),
    }
    if (resident.clientId) {
      requestData.clientId = resident.clientId
    } else if (resident.unit) {
      requestData.unit = resident.unit
    }
    if (resident.sourceUnitId) {
      requestData.unitId = resident.sourceUnitId
    }
    if (voicemailChatMessageSid) {
      requestData.voicemailChatMessageSid = voicemailChatMessageSid
    }
    if (checklistRoomItemId) {
      requestData.checklistRoomItemId = checklistRoomItemId
    } else if (inspectionId) {
      requestData.inspectionId = inspectionId
    }

    try {
      await API.createMaintenance(requestData)
      displaySnackbar(t('maintenance:form.pmsSettings.snackbar.created'))
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : t('maintenance:form.pmsSettings.snackbar.error')
      displaySnackbar(message, true)
    } finally {
      onCancel()
    }
  }

  const areAllFieldsValid =
    !!formState.description &&
    !!formState.problemId &&
    !!formState.locationId &&
    !!formState.entryNote &&
    Object.keys(formErrors).length === 0

  useEffect(() => {
    ;(async () => {
      const resp = await API.getEntrataConfig(building.id, resident.entrataPropertyId || null)
      const categoriesOptions = resp.data.categories.data.map((category) => ({
        value: category['@attributes'].Id,
        label: category['@attributes'].Name,
      }))
      const locationsOptions = resp.data.locations.data.map((priority) => ({
        label: priority['@attributes'].Name,
        value: priority['@attributes'].Id,
      }))
      const issuesOptions = resp.data.problems.data.map((problem) => ({
        label: problem['@attributes'].Name,
        value: problem['@attributes'].Id,
        ...problem,
      }))
      setEntrataConfig({
        categories: categoriesOptions,
        locations: locationsOptions,
        issues: issuesOptions,
      })
      setFilterByCategory(resp.data.filterByCategory)
      setFilterByLocation(resp.data.filterByLocation)
    })()
  }, [building, building.id])

  const issuesFiltered = useMemo(() => {
    const selectedCategoryId = formState.category ? Number(formState.category.value) : null
    const selectedLocationId = formState.locationId ? Number(formState.locationId.value) : null
    const { issues, categories } = entrataConfig

    return issues.filter((issue) => {
      // Entrata serializes ids inconsistently (number or string) — normalize before comparing
      const issueCategoryId = Number(issue['@attributes'].CategoryId)
      const issueLocations = issue.locations ? issue.locations.location : []

      if (filterByCategory && filterByLocation && categories) {
        if (selectedCategoryId && selectedLocationId) {
          return (
            issueCategoryId === selectedCategoryId &&
            issueLocations.some(
              (location) => Number(location['@attributes'].Id) === selectedLocationId,
            )
          )
        }
        if (selectedCategoryId) {
          return issueCategoryId === selectedCategoryId
        }
        if (selectedLocationId) {
          return issueLocations.some(
            (location) => Number(location['@attributes'].Id) === selectedLocationId,
          )
        }
        return true
      }

      if (filterByCategory && selectedCategoryId && categories) {
        return issueCategoryId === selectedCategoryId
      }

      if (filterByLocation && selectedLocationId) {
        return issueLocations.some(
          (location) => Number(location['@attributes'].Id) === selectedLocationId,
        )
      }

      return true
    })
  }, [filterByCategory, filterByLocation, entrataConfig, formState.category, formState.locationId])

  // Note: Reset issue field if the selected issue is not in the filtered list
  useEffect(() => {
    if (!formState.problemId) {
      return
    }

    if (
      issuesFiltered.findIndex((problemId) => problemId.value === formState.problemId.value) === -1
    ) {
      setFormState((prev) => ({ ...prev, problemId: null }))
    }
  }, [issuesFiltered])

  useEffect(() => {
    if (description) {
      setFormState((prev) => ({ ...prev, description }))
    }
  }, [description])

  return (
    <div className="maintenance-request-modal">
      <Stepper
        steps={[
          t('maintenance:form.steps.requestDetails'),
          t('maintenance:form.steps.requestSettings'),
        ]}
        currentStep={1}
      />
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.selectCategory')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={entrataConfig.categories}
          name="category"
          isClearable
          clearValue={() => setFormState((prev) => ({ ...prev, category: null }))}
          value={formState.category}
          onChange={(e) => setFormState((prev) => ({ ...prev, category: e }))}
          placeholder={t('maintenance:form.pmsSettings.selectCategory')}
        />
      </div>
      <div className="row">
        <ScheduledDateTimeFields {...scheduledDateTime.fieldProps} />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.requestDescription')}</div>
        <TextAreaField
          placeholder={t('maintenance:form.pmsSettings.descriptionPlaceholder')}
          name="description"
          value={formState.description}
          maxLength={250}
          onChange={handleFieldChange}
        />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.entrata.selectLocation')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={entrataConfig.locations}
          name="locationId"
          isClearable
          clearValue={() => setFormState((prev) => ({ ...prev, locationId: null }))}
          value={formState.locationId}
          onChange={(e) => setFormState((prev) => ({ ...prev, locationId: e }))}
          placeholder={t('maintenance:form.entrata.locationPlaceholder')}
        />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.entrata.selectIssue')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={issuesFiltered}
          name="problemId"
          value={formState.problemId}
          onChange={(e) => setFormState((prev) => ({ ...prev, problemId: e }))}
          placeholder={t('maintenance:form.entrata.issuePlaceholder')}
        />
      </div>
      <div className="row checkboxes">
        <div className="input-container">
          <ToggleField
            value={formState.pets}
            name="pets"
            label={t('maintenance:form.pmsSettings.pets')}
            labelOnRight
            onChange={handleToggleChange}
          />
        </div>
        <div className="input-container">
          <ToggleField
            name="permissionToEnter"
            label={t('maintenance:form.pmsSettings.permissionToEnter')}
            labelOnRight
            value={formState.permissionToEnter}
            onChange={handleToggleChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.entryNote.title')}</div>
        <TextAreaField
          name="entryNote"
          placeholder={t('maintenance:form.pmsSettings.entryNote.placeholder')}
          value={formState.entryNote}
          onChange={handleEntryNoteChange}
          isError={!!formErrors.entryNote}
        />
        {formErrors.entryNote && <span className={'label--error'}>{formErrors.entryNote}</span>}
      </div>
      <div className="modal-footer">
        <div className="buttons">
          <button className="button button-gray" onClick={onBack}>
            {t('maintenance:form.pmsSettings.back')}
          </button>
          <button className="button button-gray" onClick={onCancel}>
            {t('common:actions.cancel')}
          </button>
          <button
            className="button button-primary"
            disabled={!areAllFieldsValid}
            onClick={handleSubmit}
          >
            {t('maintenance:form.pmsSettings.createRequest')}
          </button>
        </div>
      </div>
    </div>
  )
}

EntrataSettings.propTypes = {
  building: PropTypes.object,
  resident: PropTypes.object,
  assignee: PropTypes.object,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  voicemailChatMessageSid: PropTypes.bool,
  inspectionId: PropTypes.number,
  description: PropTypes.string,
  checklistRoomItemId: PropTypes.number,
  onSuccess: PropTypes.func,
}

export default EntrataSettings
